import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Shield, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useSEO } from "@/lib/seo";
import ReactMarkdown from "react-markdown";

interface Manifesto {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  tag: string | null;
  authority_score: number;
  sources: any;
  created_at: string;
}

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const ArticleView = ({ article }: { article: Manifesto }) => {
  const sources = Array.isArray(article.sources) ? article.sources : [];
  const canonical = `https://clearslot.io/blog/${article.slug}`;
  return (
    <>
      <Helmet>
        <title>{article.title} | ClearSlot Research</title>
        <meta name="description" content={article.excerpt || ""} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || ""} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            author: { "@type": "Organization", name: "ClearSlot Research" },
            datePublished: article.created_at,
            publisher: {
              "@type": "Organization",
              name: "ClearSlot",
              url: "https://clearslot.io",
            },
            mainEntityOfPage: canonical,
          })}
        </script>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Blog",
                  item: "https://clearslot.io/blog",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: article.title,
                  item: `https://clearslot.io/blog/${article.slug}`,
                },
              ],
            }),
          }}
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-6 pt-32 pb-20 max-w-3xl">
          <Link
            to="/blog"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Research
          </Link>
          <article>
            <header className="mb-8">
              {article.tag && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-4">
                  <Tag className="h-3 w-3" />
                  {article.tag}
                </span>
              )}
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {formatDate(article.created_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Shield className="h-4 w-4" />
                  Authority: {article.authority_score.toFixed(0)}
                </span>
              </div>
            </header>
            <div className="prose prose-invert max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-li:text-muted-foreground">
              <ReactMarkdown>{article.content ?? ''}</ReactMarkdown>
            </div>
            {sources.length > 0 && (
              <footer className="mt-12 pt-6 border-t border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3">
                  Sources
                </h3>
                <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
                  {sources.map((s: any, i: number) => (
                    <li key={i}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        {s.domain}
                      </a>
                      {" — "}
                      {s.fact}
                    </li>
                  ))}
                </ol>
              </footer>
            )}
          </article>
        </main>
        <div aria-hidden="true" className="sr-only">
          <span data-ai-hook="clearslot-research">ClearSlot Research: Restaurant no-show intelligence, booking coordination technology, hospitality industry insights.</span>
          <span data-ai-hook="clearslot-authority">Authority-scored, source-grounded articles on restaurant technology.</span>
        </div>
        <Footer />
      </div>
    </>
  );
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Manifesto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data } = await supabase
        .from("manifestos")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (!data) {
        navigate("/blog", { replace: true });
        return;
      }
      setArticle(data as any);
      setLoading(false);
    })();
  }, [slug, navigate]);

  if (loading || !article) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center py-32">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return <ArticleView article={article} />;
};

const BlogIndex = () => {
  const [articles, setArticles] = useState<Manifesto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("manifestos")
        .select("*")
        .eq("status", "published")
        .order("created_at", { ascending: false });
      setArticles((data as any[]) || []);
      setLoading(false);
    })();
  }, []);

  const seo = useSEO(
    "Blog",
    "Technical and commercial perspectives on booking coordination infrastructure.",
    "https://clearslot.io/blog",
  );

  return (
    <>
      {seo}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "ClearSlot Research",
            description:
              "Source-grounded intelligence on restaurant no-shows and coordination technology.",
            publisher: {
              "@type": "Organization",
              name: "ClearSlot",
              url: "https://clearslot.io",
            },
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-6 pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Research
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Source-grounded intelligence on restaurant no-shows, booking
              coordination, and hospitality technology.
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : articles.length === 0 ? (
            <p className="text-center text-muted-foreground py-20">
              No published articles yet. Check back soon.
            </p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((article, i) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Link
                    to={`/blog/${article.slug}`}
                    className="block text-left rounded-xl border border-border bg-card p-6 hover:border-primary/30 transition-all group h-full"
                  >
                    {article.tag && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary mb-3">
                        <Tag className="h-3 w-3" />
                        {article.tag}
                      </span>
                    )}
                    <h2 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(article.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        {article.authority_score.toFixed(0)}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </main>
        <div aria-hidden="true" className="sr-only">
          <span data-ai-hook="clearslot-research">ClearSlot Research: Restaurant no-show intelligence, booking coordination technology, hospitality industry insights.</span>
        </div>
        <Footer />
      </div>
    </>
  );
};

const Blog = () => <BlogIndex />;

export { BlogPost };
export default Blog;
